import { useId, type ReactNode } from "react";
import { cx } from "../presentation.tsx";
import { Icon } from "../icons.tsx";

export interface ScannerFrameProps {
  /** What to hold up: "Hold your phone's code up to the camera". */
  title: ReactNode;
  /** One line under the viewfinder. */
  hint?: ReactNode;
  /** The live camera: a <video> (or a "camera off" placeholder). It fills the viewfinder. */
  children?: ReactNode;
  /** Over the viewfinder: a spinner while the decoder starts. */
  overlay?: ReactNode;
  /** Under the hint: banners (camera unavailable, a problem with the code). */
  status?: ReactNode;
  /** Bottom actions: "Find by name instead". */
  actions?: ReactNode;
  /** Shows a close button in the corner. */
  onClose?: () => void;
  closeLabel?: string;
  /** "found" turns the corners and the frame to the success colour for a moment. */
  state?: "scanning" | "found";
  /** square for QR codes, wide for a landscape preview. */
  shape?: "square" | "wide";
  /** Fixed full-screen (default) or inline inside a page. */
  inline?: boolean;
  className?: string;
}

/**
 * A full-screen camera viewfinder for scanning a QR code: dark in every theme (a camera preview
 * reads best on black), marigold corner marks, a slow scan line (off under reduced motion), the
 * instruction above and the way out below. It is a modal dialog with its own heading.
 */
export function ScannerFrame({
  title, hint, children, overlay, status, actions, onClose, closeLabel = "Close the scanner", state = "scanning", shape = "square", inline = false, className,
}: ScannerFrameProps) {
  const id = useId();
  return (
    <div
      className={cx("pmu-scanner", `pmu-scanner--${shape}`, inline && "pmu-scanner--inline", state === "found" && "pmu-scanner--found", className)}
      role="dialog" aria-modal={inline ? undefined : true} aria-labelledby={id}
    >
      {onClose && (
        <button type="button" className="pmu-scanner__close" onClick={onClose} aria-label={closeLabel}><Icon name="close" /></button>
      )}
      <h2 id={id} className="pmu-scanner__title">{title}</h2>
      <div className="pmu-scanner__view">
        <div className="pmu-scanner__media">{children}</div>
        <span className="pmu-scanner__corners" aria-hidden="true"><i /><i /><i /><i /></span>
        {state === "scanning" && <span className="pmu-scanner__line" aria-hidden="true" />}
        {overlay && <div className="pmu-scanner__overlay">{overlay}</div>}
      </div>
      {hint && <p className="pmu-scanner__hint">{hint}</p>}
      {status && <div className="pmu-scanner__status">{status}</div>}
      {actions && <div className="pmu-scanner__actions">{actions}</div>}
    </div>
  );
}
